import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, Save, Plus, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { contentService, adminService } from '../../services/api';

const AdminContactPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  
  const [content, setContent] = useState({
    // Contact Info (already exists in main content)
    phone: '+852 6187 1388',
    whatsapp: '85261871388',
    email: 'info@maidful.com.hk',
    address: '香港九龍',
    addressEn: 'Kowloon, Hong Kong',
    
    // Office Hours - Chinese
    officeHours: [
      '星期一至星期日|10:00 AM - 6:00 PM'
    ],
    // Office Hours - English
    officeHoursEn: [
      'Monday - Friday|9:00 AM - 6:00 PM',
      'Saturday|9:00 AM - 5:00 PM',
      'Sunday & Public Holidays|Closed'
    ],
    
    // Why Choose Us - Chinese
    whyChooseUs: [
      '專業團隊，豐富經驗',
      '24小時客戶支援',
      '合約期內女傭保障及勞工法例顧問',
      '透明收費，無隱藏費用'
    ],
    // Why Choose Us - English
    whyChooseUsEn: [
      'Professional team with rich experience',
      '24-hour customer support',
      'Two-year contract guarantee',
      'Free replacement service',
      'Transparent pricing, no hidden fees'
    ]
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      if (response.data) {
        // Ensure officeHoursEn matches officeHours length
        const officeHours = response.data.officeHours || [];
        const officeHoursEn = response.data.officeHoursEn || [];
        while (officeHoursEn.length < officeHours.length) {
          officeHoursEn.push('|');
        }
        
        // Ensure whyChooseUsEn matches whyChooseUs length
        const whyChooseUs = response.data.whyChooseUs || [];
        const whyChooseUsEn = response.data.whyChooseUsEn || [];
        while (whyChooseUsEn.length < whyChooseUs.length) {
          whyChooseUsEn.push('');
        }
        
        setContent(prev => ({
          phone: response.data.phone || prev.phone,
          whatsapp: response.data.whatsapp || prev.whatsapp,
          email: response.data.email || prev.email,
          address: response.data.address || prev.address,
          addressEn: response.data.addressEn || prev.addressEn,
          officeHours: officeHours.length > 0 ? officeHours : prev.officeHours,
          officeHoursEn: officeHoursEn,
          whyChooseUs: whyChooseUs.length > 0 ? whyChooseUs : prev.whyChooseUs,
          whyChooseUsEn: whyChooseUsEn,
        }));
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentContent = await contentService.get();
      const updatedContent = {
        ...currentContent.data,
        phone: content.phone,
        whatsapp: content.whatsapp,
        email: content.email,
        address: content.address,
        addressEn: content.addressEn,
        officeHours: content.officeHours,
        officeHoursEn: content.officeHoursEn,
        whyChooseUs: content.whyChooseUs,
        whyChooseUsEn: content.whyChooseUsEn,
      };
      
      await adminService.content.update(updatedContent);
      
      toast({
        title: '成功 / Success',
        description: '聯絡我們頁面內容已保存 / Contact page content saved'
      });
    } catch (error) {
      console.error('Error saving:', error);
      toast({
        title: '錯誤 / Error',
        description: '保存失敗，請再試一次 / Save failed, please try again',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Contact Info handlers
  const updateField = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  // Office Hours handlers - Chinese
  const updateOfficeHour = (index, dayOrTime, value) => {
    const newHours = [...content.officeHours];
    const parts = newHours[index].split('|');
    if (dayOrTime === 'day') {
      newHours[index] = `${value}|${parts[1] || ''}`;
    } else {
      newHours[index] = `${parts[0] || ''}|${value}`;
    }
    setContent({ ...content, officeHours: newHours });
  };

  // Office Hours handlers - English
  const updateOfficeHourEn = (index, dayOrTime, value) => {
    const newHoursEn = [...(content.officeHoursEn || [])];
    const parts = (newHoursEn[index] || '|').split('|');
    if (dayOrTime === 'day') {
      newHoursEn[index] = `${value}|${parts[1] || ''}`;
    } else {
      newHoursEn[index] = `${parts[0] || ''}|${value}`;
    }
    setContent({ ...content, officeHoursEn: newHoursEn });
  };

  const addOfficeHour = () => {
    setContent({ 
      ...content, 
      officeHours: [...content.officeHours, '|'],
      officeHoursEn: [...(content.officeHoursEn || []), '|']
    });
  };

  const removeOfficeHour = (index) => {
    setContent({ 
      ...content, 
      officeHours: content.officeHours.filter((_, i) => i !== index),
      officeHoursEn: (content.officeHoursEn || []).filter((_, i) => i !== index)
    });
  };

  // Why Choose Us handlers - Chinese
  const updateWhyChooseUs = (index, value) => {
    const newReasons = [...content.whyChooseUs];
    newReasons[index] = value;
    setContent({ ...content, whyChooseUs: newReasons });
  };

  // Why Choose Us handlers - English
  const updateWhyChooseUsEn = (index, value) => {
    const newReasonsEn = [...(content.whyChooseUsEn || [])];
    newReasonsEn[index] = value;
    setContent({ ...content, whyChooseUsEn: newReasonsEn });
  };

  const addWhyChooseUs = () => {
    setContent({ 
      ...content, 
      whyChooseUs: [...content.whyChooseUs, ''],
      whyChooseUsEn: [...(content.whyChooseUsEn || []), '']
    });
  };

  const removeWhyChooseUs = (index) => {
    setContent({ 
      ...content, 
      whyChooseUs: content.whyChooseUs.filter((_, i) => i !== index),
      whyChooseUsEn: (content.whyChooseUsEn || []).filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>聯絡我們頁面 / Contact Page</h1>
        <p className="text-gray-600 mt-2">編輯聯絡我們頁面的內容（中英文）/ Edit contact page content (Chinese & English)</p>
      </div>

      <div className="space-y-6">
        {/* Contact Information */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>聯絡資訊 / Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">電話號碼 / Phone</label>
                <Input
                  value={content.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="+852 6187 1388"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">WhatsApp 號碼</label>
                <Input
                  value={content.whatsapp}
                  onChange={(e) => updateField('whatsapp', e.target.value)}
                  placeholder="85261871388"
                />
                <p className="text-xs text-gray-500 mt-1">只需數字，不需要+號或空格</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">電郵地址 / Email</label>
                <Input
                  value={content.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="info@maidful.com.hk"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">辦公地址（中文）</label>
                <Input
                  value={content.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  placeholder="九龍尖沙咀..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-blue-700">Office Address (English)</label>
                <Input
                  value={content.addressEn || ''}
                  onChange={(e) => updateField('addressEn', e.target.value)}
                  placeholder="Tsim Sha Tsui, Kowloon..."
                  className="border-blue-300"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Office Hours */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>辦公時間 / Office Hours</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.officeHours.map((hour, index) => {
              const parts = hour.split('|');
              const partsEn = (content.officeHoursEn?.[index] || '|').split('|');
              return (
                <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                  {/* Chinese */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">日子（中文）</label>
                      <Input
                        value={parts[0] || ''}
                        onChange={(e) => updateOfficeHour(index, 'day', e.target.value)}
                        placeholder="例如: 星期一至星期五"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">時間</label>
                      <Input
                        value={parts[1] || ''}
                        onChange={(e) => updateOfficeHour(index, 'time', e.target.value)}
                        placeholder="例如: 10:00 AM - 6:00 PM"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeOfficeHour(index)}
                      className="text-red-500 mt-5"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* English */}
                  <div className="flex items-center gap-4 bg-blue-50 p-3 rounded">
                    <div className="flex-1">
                      <label className="block text-xs text-blue-600 mb-1">Day (English)</label>
                      <Input
                        value={partsEn[0] || ''}
                        onChange={(e) => updateOfficeHourEn(index, 'day', e.target.value)}
                        placeholder="e.g., Monday - Friday"
                        className="border-blue-300"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-blue-600 mb-1">Time (English)</label>
                      <Input
                        value={partsEn[1] || ''}
                        onChange={(e) => updateOfficeHourEn(index, 'time', e.target.value)}
                        placeholder="e.g., 9:00 AM - 6:00 PM"
                        className="border-blue-300"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            <Button variant="outline" onClick={addOfficeHour}>
              <Plus className="h-4 w-4 mr-2" /> 新增時段 / Add Hours
            </Button>
          </CardContent>
        </Card>

        {/* Why Choose Us */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>為什麼選擇我們？/ Why Choose Us?</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.whyChooseUs.map((reason, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={reason}
                    onChange={(e) => updateWhyChooseUs(index, e.target.value)}
                    placeholder="輸入原因（中文）..."
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeWhyChooseUs(index)}
                    className="text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <Input
                    value={content.whyChooseUsEn?.[index] || ''}
                    onChange={(e) => updateWhyChooseUsEn(index, e.target.value)}
                    placeholder="Enter reason (English)..."
                    className="border-blue-300"
                  />
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addWhyChooseUs}>
              <Plus className="h-4 w-4 mr-2" /> 新增原因 / Add Reason
            </Button>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end pb-24">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            style={{ backgroundColor: '#00b6a5' }}
            className="text-white"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Save className="h-5 w-5 mr-2" />
            )}
            保存所有更改 / Save All Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminContactPage;
